import { FontType } from "../../../../common/TextUtils"
import { Buttons } from "../../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import { Audio } from "../../../utils/Audio"
import { PhaserText } from "../../../utils/PhaserText"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { Videos } from "../../../utils/Videos"
import { TtImage } from "../TobaccoTourAssets"
import {
    DoctorNoteBullet,
    TobaccoTourMolecule,
    TobaccoTourMoleculeTypes,
} from "../TobaccoTourTypes"
import { ActivityBackground } from "./ActivitiesBackground"
import { TobaccoTourActivityScreen } from "./TobaccoTourActivityScreen"

export class TobaccoTourInteractiveScreen {
    molecules: Array<ExpandoSprite>

    private readonly backgroundGroup: Phaser.Group
    private readonly moleculeGroup: Phaser.Group
    private buttonGroup: Phaser.Group

    private selected: Array<TobaccoTourMoleculeTypes> = []
    private background: ActivityBackground
    private backgroundImage: Phaser.Sprite

    constructor(
        private game: Phaser.Game,
        background: string,
        molecules: Array<TobaccoTourMolecule>,
        private onComplete: () => void,
        audio?: string,
    ) {
        this.backgroundGroup = this.game.add.group()
        this.moleculeGroup = this.game.add.group()
        this.buttonGroup = this.game.add.group()

        if (audio) {
            this.audioInteractiveScreen(audio, background, molecules)
        }
    }

    set viewed(selected: TobaccoTourMoleculeTypes) {
        if (!this.selected.includes(selected)) {
            this.selected.push(selected)
        }

        if (this.selected.length === this.molecules.length) {
            this.onActivityComplete()
        }
    }

    private audioInteractiveScreen = (
        audio: string,
        background: string,
        molecules: Array<TobaccoTourMolecule>,
    ) => {
        this.background = TobaccoTourActivityScreen.displayScreenBackground(
            this.game,
            this.backgroundGroup,
            () => {
                this.backgroundImage = Sprites.add(this.game, background, {
                    x: Scale.x(0.215),
                    y: Scale.y(0.203),
                    group: this.backgroundGroup,
                })
                this.molecules = this.createExpandoSprites(molecules)

                Audio.add(this.game, audio, {
                    onPlay: () => {
                        this.moleculeGroup.forEach(
                            (child: ExpandoSprite) => (child.on = false),
                        )
                        PhaserUtils.disableGroup(this.moleculeGroup)
                    },
                    onStop: () => {
                        this.moleculeGroup.forEach(
                            (child: ExpandoSprite) => (child.on = true),
                        )
                        PhaserUtils.enableGroup(this.moleculeGroup)
                    },
                })
            },
        )
    }

    private createExpandoSprites = (
        molecules: Array<TobaccoTourMolecule>,
    ): Array<ExpandoSprite> =>
        molecules.map((molecule: TobaccoTourMolecule) => {
            const current = new ExpandoSprite(
                this.game,
                molecule.x,
                molecule.y,
                molecule.spriteName,
                { group: this.moleculeGroup },
            )
            current.addTitle(molecule.name, { fontType: FontType.FUTURISTIC })
            Buttons.makeSpriteButton(current, () =>
                this.moleculeOnClick(molecule, current),
            )
            return current
        })

    private moleculeOnClick = async (
        molecule: TobaccoTourMolecule,
        current: ExpandoSprite,
    ) => {
        PhaserUtils.disableGroup(this.moleculeGroup)

        this.molecules.forEach((m: ExpandoSprite) =>
            m.title.forEach((child) => (child.visible = false)),
        )

        let doctorGroup: Phaser.Group
        const timer = this.game.time.create(true)
        timer.add(
            molecule.noteStart,
            () =>
                (doctorGroup = TobaccoTourInteractiveScreen.displayDoctorsNote(
                    this.game,
                    molecule,
                )),
        )

        const video = await Videos.add(this.game, molecule.video, {
            x: Scale.x(0.215),
            y: Scale.y(0.203),
            onPlay: () => timer.start(),
            onComplete: () => {
                PhaserUtils.enableGroup(this.moleculeGroup)
                this.molecules.forEach((m: ExpandoSprite) =>
                    m.title.forEach((child) => (child.visible = true)),
                )
                doctorGroup.destroy(true)
                this.viewed = molecule.type
                current.grayOut()
                video.destroy()
            },
        })
    }

    private static displayDoctorsNote = (
        game: Phaser.Game,
        molecule: TobaccoTourMolecule,
    ): Phaser.Group => {
        const group = game.add.group()

        Sprites.add(game, TtImage.DOCTOR_NOTE, {
            group,
            x: Scale.x(0.215),
            y: Scale.y(0.203),
        })

        PhaserText.create(game, molecule.name, {
            group,
            x: Scale.x(0.4),
            y: Scale.y(0.35),
            fontPx: 20,
            weight: "bold",
        })

        const bulletCreator = PhaserText.bulletList(
            game,
            {
                group,
                x: Scale.x(0.4),
                y: Scale.y(0.4),
                fontPx: 18,
                wordWrapWidth: 0.47,
            },
            0.002,
        )
        const timer = game.time.create(true)
        molecule.noteBullets.forEach((text: DoctorNoteBullet) => {
            timer.add(text.delay, () => bulletCreator(text.text, text.indent))
        })
        timer.start()
        return group
    }

    private onActivityComplete = () => {
        this.onComplete()
        this.molecules.forEach((m: ExpandoSprite) =>
            m.title.forEach((child) => (child.visible = false)),
        )
        this.molecules.forEach((molecule) => molecule.destroy(true))
        this.backgroundImage.destroy()
        this.background.close()
        this.moleculeGroup.destroy(true)
        this.backgroundGroup.destroy(true)
    }
}
